import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  signal,
  PLATFORM_ID,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import {
  LucideAngularModule,
  CheckCircle,
  Clock,
  FileText,
  GraduationCap,
  Layout,
  Menu,
  Sparkles,
  Star,
  User,
  X,
  ArrowRight,
  ArrowDown,
  UploadCloud,
  Check,
  Zap,
  ChevronDown,
  Plus,
  Minus,
  Quote,
  Search,
  SlidersHorizontal,
  MoreHorizontal,
  Download,
  Edit3,
  Play,
} from 'lucide-angular';
import { AmplitudeService } from '../../common/analytics/amplitude.service';
import { UserTypeModalComponent, UserType } from './user-type-modal/user-type-modal';

interface FAQ {
  question: string;
  answer: string;
}

interface Testimonial {
  name: string;
  role: string;
  image: string;
  quote: string;
}

@Component({
  selector: 'app-landing',
  standalone: true,
  imports: [CommonModule, LucideAngularModule, UserTypeModalComponent],
  templateUrl: './landing.html',
  styleUrl: './landing.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LandingComponent implements OnInit {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly router = inject(Router);

  ngOnInit(): void {
    this.amplitudeService.trackEvent('landing_page_viewed');
  }

  readonly isMenuOpen = signal(false);
  readonly openFaqIndex = signal<number | null>(null);
  readonly isUserTypeModalOpen = signal(false);

  readonly icons = {
    CheckCircle,
    Clock,
    FileText,
    GraduationCap,
    Layout,
    Menu,
    Sparkles,
    Star,
    User,
    X,
    ArrowRight,
    ArrowDown,
    UploadCloud,
    Check,
    Zap,
    ChevronDown,
    Plus,
    Minus,
    Quote,
    Search,
    SlidersHorizontal,
    MoreHorizontal,
    Download,
    Edit3,
    Play,
  };

  readonly faqs: readonly FAQ[] = [
    {
      question: "Is the 'Free Forever' plan actually free?",
      answer:
        "Yes, absolutely. You get 3 scripted lesson plans and 10 auto-grading credits replenished every single week. You don't even need to enter a credit card to get started.",
    },
    {
      question: 'Can the AI really read student handwriting?',
      answer:
        "Yes! Our vision models are specifically trained on handwritten student work, ranging from messy 2nd-grade print to high school cursive. If it's legible to a human, we can usually grade it.",
    },
    {
      question: 'Is student data kept private?',
      answer:
        'Security is our top priority. We do not use your student submissions to train our public models. All data is encrypted in transit and at rest. We also recommend redacting student names for extra peace of mind.',
    },
    {
      question: 'Does this work for subjects like Art or PE?',
      answer:
        'JustTeach works for any subject with a curriculum. We have thousands of Art, Music, PE, and Foreign Language teachers using the platform to generate lesson ideas and grade theory assignments.',
    },
    {
      question: 'Can I export the lesson plans?',
      answer:
        'Yes. Once a plan is generated, you can edit it in our rich-text editor and export it directly to Google Docs, Microsoft Word, or PDF format.',
    },
  ];

  readonly testimonials: readonly Testimonial[] = [
    {
      name: 'Sarah Jenkins',
      role: 'High School English Teacher',
      image: '/images/landing/teacher2.png',
      quote:
        "I used to spend my entire Sunday grading essays. Now I'm done by Friday afternoon. The feedback is actually more detailed than what I wrote when I was tired and burnt out.",
    },
    {
      name: 'Mark Thompson',
      role: '5th Grade Science',
      image: '/images/landing/teacher1.png',
      quote:
        "The lesson planner is a lifesaver for those days when you just need a solid starting point. It understands state standards better than most textbooks I've used.",
    },
    {
      name: 'Emily Rodriguez',
      role: 'History Dept. Head',
      image: '/images/landing/teacher3.png',
      quote:
        "I was skeptical about AI, but the handwriting recognition blew me away. It read my students' cursive perfectly. This tool has literally given me my weekends back.",
    },
  ];

  readonly lessonFeatures: readonly string[] = [
    'Aligned with Common Core & State Standards',
    'Includes differentiation for diverse learners',
    'Export to PDF, Google Docs, or Word',
    'Edit and refine with a single click',
  ];

  readonly howItWorksSteps = [
    {
      title: '1. Upload or Prompt',
      desc: 'Paste your topic or upload student PDFs/images.',
      icon: 'Layout',
    },
    {
      title: '2. AI Magic',
      desc: 'Our engine analyzes content against your standards.',
      icon: 'Sparkles',
    },
    {
      title: '3. Review & Relax',
      desc: 'Approve the results and go home early.',
      icon: 'Clock',
    },
  ];

  toggleMenu(): void {
    this.isMenuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  toggleFaq(index: number): void {
    this.openFaqIndex.update((current) => (current === index ? null : index));
  }

  isFaqOpen(index: number): boolean {
    return this.openFaqIndex() === index;
  }

  scrollToSection(event: Event, sectionId: string): void {
    event.preventDefault();

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  goToAuth(): void {
    this.isUserTypeModalOpen.set(true);
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  onUserTypeSelected(userType: UserType): void {
    this.isUserTypeModalOpen.set(false);
    this.amplitudeService.trackEvent('user_type_selected', { userType });
    this.router.navigate(['/sign-up'], {
      queryParams: { userType },
    });
  }

  closeUserTypeModal(): void {
    this.isUserTypeModalOpen.set(false);
  }
}
